import { Injectable } from "@angular/core";
import { RyderNotificationService } from "@ryder/ryder-ui-library";
import { IndividualConfig, SnackbarTheme } from "../../models/individual-config.model";

enum ToastType {
    Error,
    Info,
    Success,
    Warning,
}

@Injectable({
    providedIn: 'root',
})

export class LoggerToasterService {
    constructor(private readonly ryderNotificationService: RyderNotificationService) { }

    // The options parameter in the function is for overriding toastr default options.
    // Timeout is kept in config as a placeholder until Ryder exposes a native timeout input.

    error(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.pushToast(message, title, options, ToastType.Error);
    }

    info(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.pushToast(message, title, options, ToastType.Info);
    }

    success(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.pushToast(message, title, options, ToastType.Success);
    }

    warning(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.pushToast(message, title, options, ToastType.Warning);
    }

    private readonly getDefaultTimeout = (
        // eslint-disable-next-line @typescript-eslint/default-param-last
        options: Partial<IndividualConfig> = {},
        toastType: ToastType,
    ): Partial<IndividualConfig> => {
        if (!options.timeout && options.timeout !== 0) {
            switch (toastType) {
                case ToastType.Error:
                    return { ...options, timeout: 5000 };
                case ToastType.Success:
                    return { ...options, timeout: 2000 };
                case ToastType.Warning:
                    return { ...options, timeout: 4000 };
                case ToastType.Info:
                    return { ...options, timeout: 3000 };
                default:
                    return { ...options, timeout: 5000 };
            }
        }
        return options;
    };

    clearToast(toast: number): void {
        void toast;
    }

    clearAllToasts(): void {
        // RyderNotificationService currently exposes show only.
    }

    private readonly pushToast = (
        message: string,
        title: string | undefined,
        options: Partial<IndividualConfig> | undefined,
        toastType: ToastType,
    ): void => {
        const defaultTimeoutoptions = this.getDefaultTimeout(options, toastType);
        this.ryderNotificationService.showSnackbar(
            title ? `${title}: ${message}` : message,
            this.mapToastTypeToTheme(toastType),
            defaultTimeoutoptions.removable !== false,
            !!defaultTimeoutoptions.action,
            defaultTimeoutoptions.actionText,
            defaultTimeoutoptions.timeout,
        );
    };

    private readonly mapToastTypeToTheme = (toastType: ToastType): SnackbarTheme => {
        switch (toastType) {
            case ToastType.Error:
                return 'error';
            case ToastType.Warning:
                return 'warning';
            case ToastType.Success:
                return 'success';
            case ToastType.Info:
            default:
                return 'default';
        }
    };
}